import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { Skeleton, TablePagination } from "@mui/material";
import TablePaginationActions from "@commons/Table/TableContent/TablePaginationActions";
import NotificationCard from "../components/NotificationCard";
import NotificationFilter from "../components/NotificationFilter";
import Text from "@commons/Typography/Text";
import type { SystemNotification } from "@typings/entities/notifications";
import type { NotificationFilters } from "@services/notifications/list/type";
import { useGetNotifications } from "@services/notifications/list";
import { getNotificationRoute } from "@utils/translator/notifications";
import { config } from "@/config";

function NotificationsScreen() {
  const navigate = useNavigate();
  const [page, setPage] = useState(0);
  const [filters, setFilters] = useState<NotificationFilters>({
    page: 1,
    limit: config.table.defaultPageSize,
  });

  const { data, isLoading, error } = useGetNotifications(filters);

  useEffect(() => {
    if (error) {
      toast.error("Erro ao carregar notificações");
    }
  }, [error]);

  const handleFilterChange = useCallback(
    (field: keyof NotificationFilters, value: string) => {
      setPage(0);
      setFilters({
        ...filters,
        [field]: value || undefined,
        page: 1,
      });
    },
    [filters],
  );

  const handleApplyFilters = useCallback(() => {
    setPage(0);
    setFilters({
      ...filters,
      page: 1,
    });
  }, [filters]);

  const handlePageChange = useCallback(
    (_event: React.MouseEvent<HTMLButtonElement> | null, newPage: number) => {
      setPage(newPage);
      setFilters({
        ...filters,
        page: newPage + 1,
      });
      window.scrollTo({ top: 0, behavior: "smooth" });
    },
    [filters],
  );

  const handleRowsPerPageChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const newLimit = parseInt(event.target.value, 10);
      setPage(0);
      setFilters({
        ...filters,
        page: 1,
        limit: newLimit,
      });
    },
    [filters],
  );

  const handleViewDetails = useCallback(
    async (notification: SystemNotification) => {
      try {
        const route = getNotificationRoute(notification);
        navigate(route);
      } catch {
        toast.error("Erro ao abrir detalhes da notificação");
      }
    },
    [navigate],
  );

  const renderNotifications = () => {
    if (isLoading) {
      return (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {Array.from({ length: 6 }).map((_, index) => (
            <Skeleton
              key={index}
              variant="rectangular"
              className="rounded-xl h-36"
            />
          ))}
        </div>
      );
    }

    if (!data || data.items.length === 0) {
      return (
        <div className="bg-white rounded-lg shadow-md p-8 text-center">
          <Text size="lg" className="text-gray-500">
            Nenhuma notificação encontrada
          </Text>
        </div>
      );
    }

    return (
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {data.items.map((notification) => (
          <NotificationCard
            key={notification.id}
            notification={notification}
            onViewDetails={handleViewDetails}
          />
        ))}
      </div>
    );
  };

  return (
    <div className="space-y-4">
      <NotificationFilter
        filters={filters}
        onChange={handleFilterChange}
        onApply={handleApplyFilters}
      />

      {renderNotifications()}

      {data && data.items && data.items.length > 0 && (
        <div className="w-full flex justify-end mt-6">
          <TablePagination
            component="div"
            rowsPerPageOptions={config.table.pageSizeOptions}
            count={data.meta.totalItems}
            rowsPerPage={filters.limit || config.table.defaultPageSize}
            page={page}
            labelRowsPerPage="Linhas por página:"
            labelDisplayedRows={({ from, to, count }) =>
              `${from}-${to} de ${count !== -1 ? count : `mais de ${to}`}`
            }
            onPageChange={handlePageChange}
            onRowsPerPageChange={handleRowsPerPageChange}
            ActionsComponent={TablePaginationActions}
            sx={{
              ".MuiTablePagination-selectLabel": {
                marginRight: "12px",
              },
              ".MuiTablePagination-select": {
                marginRight: "24px",
              },
            }}
          />
        </div>
      )}
    </div>
  );
}

export default NotificationsScreen;
